import React, { useState, useCallback, memo } from "react";
import { Button, Icon, Text } from "@chakra-ui/react";
import { FaRegCircleDot, FaRegCircle, FaCircleCheck } from "react-icons/fa6";
import { FaClock, FaCircleHalfStroke, FaCircleXmark } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import {
  backgroundGrey,
  buttonAltRed,
  buttonGreen,
  buttonGreenText,
  buttonRedText,
  color1,
  color2,
  color3,
  color4,
  color6,
  color8,
  newBorder,
  primary,
} from "../../utils/colors";
import {
  buttonBlue,
  buttonBlueDark,
  buttonYellow,
  buttonYellowDark,
  buttonGreenDark,
  buttonRed,
  buttonRedDark,
  buttonAltGrey,
  buttonAltGreen,
  buttonAltYellow,
  buttonAltSuccess,
  textPrimary,
  white,
  buttonGreenVividDark,
  textSecondary,
  text,
} from "../../utils/colors";
import { shade } from "polished";

const StatusButtonNewLite = ({ currentStatus, options, ...props }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  if (options === "patient") {
    options = [
      {
        value: "new",
        label: t("patientStatus.new"),
        icon: FaRegCircleDot,
        bg: color8,
        bgDark: buttonBlueDark,
        color: textPrimary,
        borderColor: buttonBlue,
        bgHover: "#b6cce1",
      },
      {
        value: "inactive",
        label: t("patientStatus.inactive"),
        icon: FaRegCircle,
        bg: buttonAltGrey,
        bgDark: textSecondary,
        color: text,
        borderColor: buttonAltGrey,
        bgHover: "#ddd",
      },

      {
        value: "planningTreatment",
        label: t("patientStatus.planningTreatment"),
        icon: FaClock,
        bg: color2,
        bgDark: buttonYellowDark,
        color: white,
        borderColor: buttonYellow,
        bgHover: "#9ebfc2",
      },
      {
        value: "approvedTreatment",
        label: t("patientStatus.approvedTreatment"),
        icon: FaCircleCheck,
        bg: color3,
        bgDark: buttonGreenDark,
        color: white,
        borderColor: buttonAltGreen,
        bgHover: "#9ebfc2",
      },
      {
        value: "ongoingTreatment",
        label: t("patientStatus.ongoingTreatment"),
        icon: FaCircleHalfStroke,
        bg: color4,
        bgDark: buttonYellowDark,
        color: white,
        borderColor: buttonAltYellow,
        bgHover: "#9ebfc2",
      },
      {
        value: "cancelledTreatment",
        label: t("patientStatus.cancelledTreatment"),
        icon: FaCircleXmark,
        bg: color6,
        bgDark: buttonRedDark,
        color: white,
        borderColor: buttonRed,
        bgHover: "#dda3a3",
      },
      {
        value: "completedTreatment",
        label: t("patientStatus.completedTreatment"),
        icon: FaCircleCheck,
        bg: buttonAltSuccess,
        bgDark: buttonGreenVividDark,
        color: textPrimary,
        borderColor: buttonAltSuccess,
        bgHover: "#dda3a3",
      },
    ];
  } else if (options === "employee") {
    options = [
      {
        value: "active",
        label: t("employeeStatus.active"),
        icon: FaRegCircleDot,
        bg: color3,
        bgDark: buttonGreenDark,
        color: white,
        borderColor: buttonGreen,
        bgHover: "#9ebfc2",
      },
      {
        value: "inactive",
        label: t("employeeStatus.inactive"),
        icon: FaRegCircle,
        bg: buttonAltGrey,
        bgDark: textSecondary,
        color: text,
        borderColor: buttonAltGrey,
        bgHover: "#ddd",
      },
    ];
  } else if (options === "user") {
    options = [
      {
        value: "active",
        label: t("status.active"),
        icon: FaRegCircleDot,
        bg: color3,
        bgDark: buttonGreenDark,
        color: white,
        borderColor: buttonGreen,
        bgHover: "#9ebfc2",
      },
      {
        value: "inactive",
        label: t("status.inactive"),
        icon: FaRegCircle,
        bg: buttonAltGrey,
        bgDark: textSecondary,
        color: text,
        borderColor: buttonAltGrey,
        bgHover: "#ddd",
      },
      {
        value: "invited",
        label: t("status.invited"),
        icon: FaRegCircle,
        bg: buttonAltGrey,
        bgDark: textSecondary,
        color: text,
        borderColor: buttonAltGrey,
        bgHover: "#ddd",
      },
    ];
  } else if (options === "invoice") {
    options = [
      {
        value: "draft",
        label: t("status.invoiceDraft"),
        icon: FaRegCircle,
        bg: buttonAltGrey,
        bgDark: textSecondary,
        color: textSecondary,
        borderColor: newBorder,
        bgHover: "#ddd",
      },
      {
        value: "open",
        label: t("status.invoiceOpen"),
        icon: FaRegCircle,
        bg: buttonAltGrey,
        bgDark: textSecondary,
        color: textSecondary,
        borderColor: newBorder,
        bgHover: "#ddd",
      },
      {
        value: "paid",
        label: t("status.invoicePaid"),
        icon: FaCircleCheck,
        bg: buttonAltSuccess,
        bgDark: buttonGreenDark,
        color: buttonGreenText,
        borderColor: buttonGreen,
        bgHover: "#9ebfc2",
      },
      {
        value: "void",
        label: t("status.invoiceVoid"),
        icon: FaCircleXmark,
        bg: buttonAltRed,
        bgDark: buttonRedDark,
        color: buttonRedText,
        borderColor: buttonRed,
        bgHover: "#dda3a3",
      },
      {
        value: "uncollectible",
        label: t("status.invoiceUncollectible"),
        icon: FaCircleXmark,
        bg: buttonAltRed,
        bgDark: buttonRedDark,
        color: buttonRedText,
        borderColor: buttonRed,
        bgHover: "#dda3a3",
      },
    ];
  }

  const currentStatusOption = options.find(
    (option) => option.value === currentStatus
  );

  return (
    <Button
      {...props}
      as={Button}
      onClick={handleClick}
      // size="md"
      // h={isSmall ? "2.25rem" : "2.5rem"}
      size={props.size || "md"} // fallback to "md" if none provided
      p={props.p || "1rem 1rem"} // same logic, or just omit px entirely
      w={props.w || "initial"} // same logic, or just omit px entirely
      bg={currentStatusOption ? currentStatusOption.bg : white}
      color={currentStatusOption ? currentStatusOption.color : textSecondary}
      display="flex"
      alignItems="center"
      justifyContent="center"
      _hover={{
        bg: shade(0.3, currentStatusOption ? currentStatusOption.bg : white),
        color: white,
      }}
      borderRadius="0.75rem"
      _active={{
        bg: currentStatusOption ? currentStatusOption.bg : white,
        color: currentStatusOption ? currentStatusOption.color : textSecondary,
      }}
      // px="5rem"

      // maxW="100px"
      // isDisabled={isDisabled}
    >
      {currentStatusOption && (
        <>
          <Icon
            as={currentStatusOption.icon}
            mr={1}
            fontSize="0.75rem"
            opacity="1"
            position="relative"
            // top="-1px"
          />

          <Text fontSize="0.875rem" fontWeight="500" lineHeight="1">
            {currentStatusOption?.label}
          </Text>
        </>
      )}
    </Button>
  );
};

export default memo(StatusButtonNewLite);
